<script setup lang="ts">
import { useConfirm } from 'primevue/useconfirm'
import {
  getAdminListJobFunctionCustomsQueryKey,
  useAdminListJobFunctionCustoms,
  useAdminRejectJobFunctionCustom,
  useAdminUnrejectJobFunctionCustom,
  adminClassifyJobFunctionCustomIsco,
  useListJobCategories,
} from '@swipe4work/api-orval'
import type { AdminListJobFunctionCustomJobFunctionCustomDto } from '@swipe4work/models-orval'
import { CustomRejectionStatus } from '@swipe4work/models-orval'
import { useQueryClient } from '@tanstack/vue-query'
import { useToast } from '@/shared/composables/UseToast'
import { useDynamicDialog } from '@/shared/composables/UseDynamicDialog'
import PromoteJobFunctionDialog from '../components/PromoteJobFunctionDialog.vue'
import IscoCodePickerDialog from '../dialogs/IscoCodePickerDialog.vue'

definePage({
  path: '/data/job-function-customs',
  name: '/data/JobFunctionCustoms',
  meta: {
    title: 'Custom functies',
  },
})

// Job categories map for resolving IDs to names in the table.
const { data: jobCategoriesData } = useListJobCategories()
const jobCategoryMap = computed(() => {
  const map = new Map<number, string>()

  for (const cat of jobCategoriesData.value?.jobCategories ?? []) {
    map.set(cat.id, cat.name)
  }

  return map
})

function getCategoryName(id: number): string {
  return jobCategoryMap.value.get(id) ?? String(id)
}

// Pagination, search & status filter
const search = ref('')
const limit = ref(20)
const offset = ref(0)
const first = ref(0)

// Openstaand (pending) is the default working view; Afgewezen / Alle are opt-in.
const status = ref<CustomRejectionStatus>(CustomRejectionStatus.Pending)
const statusOptions = [
  {
    label: 'Openstaand',
    value: CustomRejectionStatus.Pending,
  },
  {
    label: 'Afgewezen',
    value: CustomRejectionStatus.Rejected,
  },
  {
    label: 'Alle',
    value: CustomRejectionStatus.All,
  },
]

const idealOnly = ref(false)

function onStatusChange() {
  offset.value = 0
  first.value = 0
}

const listParams = computed(() => ({
  search: search.value || undefined,
  limit: limit.value,
  offset: offset.value,
  status: status.value,
  isIdeal: idealOnly.value || undefined,
}))

const { data: listData, isPending, isError } = useAdminListJobFunctionCustoms(listParams)

const jobFunctionCustoms = computed(() => listData.value?.jobFunctionCustoms ?? [])
const totalRecords = computed(() => listData.value?.totalCount ?? 0)

let searchTimeout: ReturnType<typeof setTimeout> | null = null

function onSearchInput(value: string | undefined) {
  if (searchTimeout) { clearTimeout(searchTimeout) }
  searchTimeout = setTimeout(() => {
    search.value = value ?? ''
    offset.value = 0
    first.value = 0
  }, 300)
}

function onPage(event: {
  first: number
  rows: number
}) {
  first.value = event.first
  offset.value = event.first
  limit.value = event.rows
}

const promotingRow = ref<AdminListJobFunctionCustomJobFunctionCustomDto | null>(null)

// Reject / un-reject: a non-destructive soft-state toggled via the app-wide confirm popup.
const confirm = useConfirm()
const toast = useToast()
const queryClient = useQueryClient()
const dialog = useDynamicDialog()

// Distinct from "Promoveren" — reassigns the ISCO code without touching promotion state.
function openIscoPicker(
  item: AdminListJobFunctionCustomJobFunctionCustomDto,
  suggestion?: {
    code: string,
    label: string | null,
    score: number | null,
    source: string
  } | null,
) {
  dialog.open(IscoCodePickerDialog, {
    props: {
      header: 'ISCO-code toewijzen',
      style: {
        width: '90vw',
        height: '90vw',
      },
    },
    data: {
      entityType: 'jobFunctionCustom',
      entityId: item.id,
      entityName: item.canonicalName,
      currentIsco4: item.isco4 ?? null,
      suggestion,
    },
  })
}

const classifyingId = ref<number | null>(null)

async function classifyIsco(item: AdminListJobFunctionCustomJobFunctionCustomDto) {
  classifyingId.value = item.id
  try {
    const result = await adminClassifyJobFunctionCustomIsco(item.id)
    if (!result.isco4) {
      toast.error('Geen suggestie', 'De AI kon geen ISCO-code voorstellen voor deze custom functie.')

      return
    }

    openIscoPicker(item, {
      code: result.isco4,
      label: result.escoLabel ?? result.iscoUnitGroup?.preferredLabel ?? null,
      score: result.score ?? null,
      source: result.source,
    })
  }
  catch {
    toast.error('Fout', 'Er is iets misgegaan bij het classificeren.')
  }
  finally {
    classifyingId.value = null
  }
}

function invalidateList() {
  queryClient.invalidateQueries({ queryKey: getAdminListJobFunctionCustomsQueryKey() })
}

const rejectMutation = useAdminRejectJobFunctionCustom({
  mutation: {
    onSuccess: () => {
      toast.success('Afgewezen', 'De custom functie is afgewezen.')
      invalidateList()
    },
    onError: () => {
      toast.error('Fout', 'Er is iets misgegaan bij het afwijzen.')
    },
  },
})

const unrejectMutation = useAdminUnrejectJobFunctionCustom({
  mutation: {
    onSuccess: () => {
      toast.success('Hersteld', 'De custom functie staat weer open.')
      invalidateList()
    },
    onError: () => {
      toast.error('Fout', 'Er is iets misgegaan bij het herstellen.')
    },
  },
})

function confirmReject(item: AdminListJobFunctionCustomJobFunctionCustomDto) {
  confirm.require({
    header: 'Custom functie afwijzen',
    icon: 'fa-regular fa-ban',
    message: `"${item.canonicalName}" afwijzen? De functie verdwijnt uit de openstaande lijst en wordt niet meer voorgesteld door de AI. Je kunt dit later herstellen.`,
    acceptLabel: 'Afwijzen',
    rejectLabel: 'Annuleren',
    acceptProps: { severity: 'danger' },
    accept: () => rejectMutation.mutate({ id: item.id }),
  })
}

function confirmUnreject(item: AdminListJobFunctionCustomJobFunctionCustomDto) {
  confirm.require({
    header: 'Custom functie herstellen',
    icon: 'fa-regular fa-rotate-left',
    message: `"${item.canonicalName}" terugzetten naar openstaand?`,
    acceptLabel: 'Herstellen',
    rejectLabel: 'Annuleren',
    accept: () => unrejectMutation.mutate({ id: item.id }),
  })
}
</script>

<template>
  <div class="space-y-5">
    <Card>
      <template #content>
        <div class="flex flex-col gap-4">
          <h3 class="text-lg font-semibold">
            Custom functies
          </h3>

          <!-- Search + status filter -->
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <IconField class="w-full sm:max-w-md">
              <InputIcon class="fa-regular fa-search" />
              <InputText
                :model-value="search"
                placeholder="Zoeken op naam..."
                class="w-full"
                @update:model-value="onSearchInput"
              />
            </IconField>

            <SelectButton
              v-model="status"
              :options="statusOptions"
              option-label="label"
              option-value="value"
              :allow-empty="false"
              aria-label="Status filter"
              @change="onStatusChange"
            />
            <label class="flex cursor-pointer items-center gap-2 text-sm text-surface-700">
              <ToggleSwitch v-model="idealOnly" input-id="ideal-only" @update:model-value="onStatusChange" />
              Alleen Ideal-functies
            </label>
          </div>

          <!-- Table -->
          <div v-if="isPending" class="flex flex-col gap-2">
            <Skeleton v-for="n in 5" :key="n" height="3rem" />
          </div>
          <Message v-else-if="isError" severity="error">
            Er is iets misgegaan bij het laden van de custom functies.
          </Message>
          <DataTable
            v-else
            :value="jobFunctionCustoms"
            :total-records="totalRecords"
            :first="first"
            :rows="limit"
            :loading="isPending"
            class="w-full"
            paginator
            lazy
            :rows-per-page-options="[10, 20, 50, 100]"
            @page="onPage"
          >
            <Column field="canonicalName" header="Naam" />
            <Column field="normalizedName" header="Genormaliseerd" class="hidden sm:table-cell">
              <template #body="{ data: item }">
                <span class="text-sm text-surface-500">{{ item.normalizedName }}</span>
              </template>
            </Column>
            <Column field="count" header="Aantal" />
            <Column field="isco4" header="ISCO-4">
              <template #body="{ data: item }">
                <template v-if="item.isco4">
                  <span class="text-sm font-mono">{{ item.isco4 }}</span>
                  <i
                    v-if="item.isIdeal"
                    v-tooltip.top="'Ideal-functie: ISCO-groep is een Ideal Occupation'"
                    class="fa-solid fa-star ml-2 text-xs text-primary"
                  />
                </template>
                <span v-else class="text-sm text-surface-400">—</span>
              </template>
            </Column>
            <Column header="Categorieën">
              <template #body="{ data: item }">
                <div v-if="item.jobCategoryIds.length > 0" class="flex flex-wrap gap-1">
                  <Tag
                    v-for="id in item.jobCategoryIds"
                    :key="id"
                    :value="getCategoryName(id)"
                    severity="secondary"
                  />
                </div>
                <span v-else class="text-sm text-surface-400">—</span>
              </template>
            </Column>
            <Column header="Status" style="width: 120px">
              <template #body="{ data: item }">
                <Tag
                  v-if="item.rejectedAt"
                  value="Afgewezen"
                  severity="danger"
                />
                <Tag
                  v-else
                  value="Openstaand"
                  severity="secondary"
                />
              </template>
            </Column>
            <Column header="Acties" style="width: 360px">
              <template #body="{ data: item }">
                <div class="flex gap-2">
                  <Button
                    label="Promoveren"
                    icon="fa-regular fa-arrow-up-right-dots"
                    size="small"
                    @click="promotingRow = item"
                  />
                  <Button
                    icon="fa-regular fa-sitemap"
                    severity="secondary"
                    size="small"
                    v-tooltip.top="'ISCO-code toewijzen'"
                    @click="openIscoPicker(item)"
                  />
                  <Button
                    icon="fa-regular fa-sparkles"
                    severity="secondary"
                    size="small"
                    v-tooltip.top="'Classificeren met AI'"
                    :loading="classifyingId === item.id"
                    @click="classifyIsco(item)"
                  />
                  <Button
                    v-if="!item.rejectedAt"
                    label="Afwijzen"
                    icon="fa-regular fa-ban"
                    severity="danger"
                    outlined
                    size="small"
                    @click="confirmReject(item)"
                  />
                  <Button
                    v-else
                    label="Herstellen"
                    icon="fa-regular fa-rotate-left"
                    severity="secondary"
                    size="small"
                    @click="confirmUnreject(item)"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </template>
    </Card>

    <PromoteJobFunctionDialog v-model:row="promotingRow" />
  </div>
</template>
